import { Label } from "components/label";
import { CollapsibleSection } from "../../../../../../components/collapsibleSection";
import Input from "components/input";
import Select from "components/select";
import { Controller, UseFormReturn } from "react-hook-form";
import { PHONE_CODE } from "utills/constants";
import { HttpError } from "@refinedev/core";
import DatePicker from "components/datePicker";
import moment from "moment";
import FilesDropzone from "../../filesDropzone";
import { XIcon } from "@heroicons/react/outline";
import { Link } from "react-router-dom";
import { getPixFileName } from "utills/helpers";
import { IEditOrgForm } from "interfaces";

export const SellerIdentificationSection = (
  props: UseFormReturn<IEditOrgForm, HttpError, IEditOrgForm>
) => {
  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = props;
  const _addLicense = (licenseUrl: string) => {
    const updatedLicenses = watch("seller_business_license");
    updatedLicenses.push(licenseUrl);
    setValue("seller_business_license", updatedLicenses);
  };
  const _removeLicense = (index: number) => {
    let updatedLicenses = watch("seller_business_license");
    updatedLicenses.splice(index, 1);
    setValue("seller_business_license", updatedLicenses);
  };

  return (
    <CollapsibleSection
      label="Seller Identification"
      hint="Merchant's identification information based on Business Registration or Establishment License"
    >
      <div className="divide-y divide-gray-100">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
            <div className="col-span-full">
              <Label
                id="seller_business_license"
                name="Image or scanned copy of the business license"
              />
              <Controller
                control={control}
                name="seller_business_license"
                defaultValue={[]}
                render={({ field: { value = [] } }) => {
                  return (
                    <div>
                      <FilesDropzone
                        id="seller_business_license"
                        error={errors["seller_business_license"]}
                        value={value}
                        onChange={(url: string) => _addLicense(url)}
                      />
                    </div>
                  );
                }}
              />
              <div className="flex items-center flex-wrap gap-x-2">
                {(watch("seller_business_license") ?? []).map(
                  (licenseUrl, index) => (
                    <Link to={licenseUrl} target="_blank" key={index}>
                      <div className="mt-2 w-fit flex items-center pl-3 pr-0 bg-magnolia-500 ring-magnolia-500 text-brand-500 text-xs rounded-full font-medium break-all">
                        <p className="align-middle">
                          {getPixFileName(licenseUrl)}
                        </p>
                        <div
                          className="shrink-0 px-2.5 py-1.5 hover:cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            _removeLicense(index);
                          }}
                        >
                          <XIcon className="shrink-0 w-4 h-4" />
                        </div>
                      </div>
                    </Link>
                  )
                )}
              </div>
              {errors["seller_business_license"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_business_license"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label required id="seller_company_name" name="Company Name" />
              <Input
                id="seller_company_name"
                type="text"
                placeholder="Company Name"
                error={errors["seller_company_name"]}
                {...register("seller_company_name")}
              />
              {errors["seller_company_name"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_company_name"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label id="seller_company_tax" name="Company Tax ID" required />
              <Input
                id="seller_company_tax"
                type="text"
                placeholder="Company Tax ID"
                error={errors["seller_company_tax"]}
                {...register("seller_company_tax")}
              />
              {errors["seller_company_tax"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_company_tax"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="seller_business_certificate_number"
                name="Business/ Establishment certificate number"
                required
              />
              <Input
                id="seller_business_certificate_number"
                type="text"
                placeholder="Business/ Establishment certificate number"
                error={errors["seller_business_certificate_number"]}
                {...register("seller_business_certificate_number")}
              />
              {errors["seller_business_certificate_number"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_business_certificate_number"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label id="seller_business_issue_date" name="Issue date" />
              <Controller
                control={control}
                name="seller_business_issue_date"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <DatePicker
                      id="seller_business_issue_date"
                      placeholder="Issue date"
                      className="bg-white"
                      error={errors["seller_business_issue_date"]}
                      value={value}
                      onChange={(date: any) =>
                        date === null
                          ? onChange("")
                          : onChange(moment(date).format("YYYY-MM-DD"))
                      }
                    />
                  );
                }}
              />
              {errors["seller_business_issue_date"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_business_issue_date"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label id="seller_place_of_issue" name="Place of issue" />
              <Input
                id="seller_place_of_issue"
                type="text"
                placeholder="Place of issue"
                error={errors["seller_place_of_issue"]}
                {...register("seller_place_of_issue")}
              />
              {errors["seller_place_of_issue"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_place_of_issue"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="seller_business_address"
                name="Business address"
                required
              />
              <Input
                id="seller_business_address"
                type="text"
                placeholder="Business address"
                error={errors["seller_business_address"]}
                {...register("seller_business_address")}
              />
              {errors["seller_business_address"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_business_address"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label id="seller_company_email" name="Company Email" required />
              <Input
                id="seller_company_email"
                type="text"
                placeholder="Company Email"
                error={errors["seller_company_email"]}
                {...register("seller_company_email")}
              />
              {errors["seller_company_email"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_company_email"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label id="seller_company_phone" name="Company phone" required />
              <div className="flex">
                <Controller
                  control={control}
                  name="seller_company_phone_area_code"
                  defaultValue="84"
                  render={({ field: { onChange, value = "", ref } }) => {
                    const areaPhoneCodeArr = PHONE_CODE.map((item) => ({
                      label: item.label,
                      value: item.code,
                    }));
                    return (
                      <Select
                        ref={ref}
                        className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                        options={areaPhoneCodeArr}
                        id="seller_company_phone_area_code"
                        value={areaPhoneCodeArr.find((c) => c.value === value)}
                        onChange={(selected: {
                          label: string;
                          value: string;
                        }) => onChange(selected?.value)}
                        error={errors["seller_company_phone_area_code"]}
                      />
                    );
                  }}
                />
                <Input
                  id="seller_company_phone"
                  type="phone"
                  className={`${
                    errors["seller_company_phone"]
                      ? "border-l-[1px] focus:!ml-0"
                      : "border-l-0"
                  } rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                  placeholder="Company phone"
                  error={errors["seller_company_phone"]}
                  {...register("seller_company_phone")}
                />
              </div>
              {errors["seller_company_phone_area_code"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_company_phone_area_code"]?.message}
                  </span>
                </p>
              ) : null}
              {errors["seller_company_phone"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["seller_company_phone"]?.message}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};
